// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useCallback } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { CredentialBilling } from '@daml.js/utility-credential-app-v0/lib/Utility/Credential/App/V0/Model/Billing';
import { useModuleParty } from '../../contexts/ModuleContext';
import useBillCredential from '../../hooks/mutations/credential/billing/useBillCredential';
import useCancelCredentialBilling from '../../hooks/mutations/credential/billing/useCancelCredentialBilling';
import useMutate from '../../hooks/mutations/useMutate';
import useCoins from '../../hooks/queries/cn/useCoin';
import useUserService from '../../hooks/queries/credential/onboarding/useUserService';
import TemplateContract from '../../utils/TemplateContract';
import {
  ERR_BILL_CRED,
  ERR_CAN_BILL_CRED,
  SUC_BILL_CRED,
  SUC_CAN_BILL_CRED,
} from '../../utils/strings';
import { GenericAction } from '../collateral/collaterals/types';

type Row = TemplateContract<CredentialBilling>;

type ActionsCallbacks = {
  handleDistributeClick: (r: Row) => Promise<unknown> | void;
  handleTopUpClick: (r: Row) => Promise<unknown> | void;
};

export const useCreateBillingActions = ({
  handleDistributeClick,
  handleTopUpClick,
}: ActionsCallbacks) => {
  const party = useModuleParty();
  const userService = useUserService();

  const billCredential = useBillCredential();
  const cancelBilling = useCancelCredentialBilling();

  const mutate = useMutate();
  const amulets = useCoins();
  const { showBoundary } = useErrorBoundary<Error>();

  const billAction = useCallback(
    (r: Row) => {
      const payload = {
        credentialBillingCid: r.contractId,
      };

      return mutate(billCredential, payload, SUC_BILL_CRED, ERR_BILL_CRED);
    },
    [billCredential, mutate],
  );

  const cancelAction = useCallback(
    (r: Row) => {
      if (!userService.data) {
        showBoundary(new Error(ERR_CAN_BILL_CRED, { cause: 'User service is not available.' }));
        return Promise.resolve();
      }

      const payload = {
        userServiceCid: userService.data.contractId,
        credentialBillingCid: r.contractId,
      };

      return mutate(cancelBilling, payload, SUC_CAN_BILL_CRED, ERR_CAN_BILL_CRED);
    },
    [userService.data, cancelBilling, mutate, showBoundary],
  );

  const createActions = (r: Row) => {
    const actions: GenericAction<Row>[] = [];

    if (r.payload.operator === party) {
      actions.push({
        name: 'Bill',
        action: billAction,
      });
    }

    if (r.payload.issuer === party || r.payload.holder === party) {
      actions.push({ name: 'Cancel', action: cancelAction });
    }

    if (r.payload.issuer === party) {
      actions.push({
        name: 'Distribute',
        action: handleDistributeClick,
      });
    }

    const areAmuletsAvailable = amulets?.data?.length !== 0;

    if (r.payload.holder === party) {
      actions.push({
        name: areAmuletsAvailable ? 'Top Up' : 'Balance Too Low',
        action: handleTopUpClick,
        disabled: !areAmuletsAvailable,
      });
    }

    return actions;
  };

  return { createActions };
};
